import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { recipientsSaved, recipientsSaveError } from './actions/recipients.actions';

/**
 * Dispatches a recipients write request and returns a **cold** observable that settles once
 * the matching `recipientsSaved` / `recipientsSaveError` outcome arrives — mirroring
 * `TimesheetAndExpensesNgrxService.dispatchSaveRequest`. Lives here, as a plain exported
 * function rather than a service method, so `RecipientsNgrxService` has zero private
 * helpers; deleting the service later loses nothing.
 *
 * The outcome subscription is set up *before* dispatching, so a synchronously flushed effect
 * result cannot be missed.
 *
 * CRITICAL — must stay cold: `ConfirmationServiceReportModel.updateRecipientEntriesFromLocalStorage`
 * calls `saveRecipientsByNotificationId(...)` and never subscribes to the result, so today
 * that particular write silently never happens. A cold bridge (dispatch-on-subscribe)
 * preserves that exactly; an eager dispatch would change behaviour under the
 * `enableNgRx` flag. (Worth a ticket to fix the call site; out of scope here.)
 *
 * @param store the root store to dispatch the request on
 * @param actions$ the actions stream to observe the settle outcome on
 * @param request the write action to dispatch (`saveRecipientsRequested` or `removeRecipientsRequested`)
 * @param correlationId the id carried by `request` and echoed by the settle outcome
 * @returns an observable that completes on `recipientsSaved` and errors on `recipientsSaveError`
 */
export function dispatchRecipientsWrite(
    store: Store,
    actions$: Actions,
    request: Action,
    correlationId: string,
): Observable<unknown> {
    return new Observable((subscriber) => {
        const subscription = actions$
            .pipe(
                ofType(recipientsSaved, recipientsSaveError),
                filter((action) => action.correlationId === correlationId),
                take(1),
            )
            .subscribe((action) => {
                if (action.type === recipientsSaveError.type) {
                    subscriber.error(new Error(action.error));
                    return;
                }
                subscriber.next(undefined);
                subscriber.complete();
            });
        store.dispatch(request);
        return (): void => subscription.unsubscribe();
    });
}
